import { useLayoutEffect, useRef, useState } from 'react';
import { NavLink } from "react-router-dom";
import MenuItem from './HeaderMenuItem';
import SectionLink from '../components/SectionLink';
import { Routes } from '../routes/RouteConstants';
import {
  AppBar,
  Button,
  Stack,
  Container,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  Drawer,
  keyframes,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CollectionsIcon from '@mui/icons-material/Collections';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { useTenant } from '../contexts/TenantContext';
import useRevealOnScroll, { REVEAL_EASING } from '../hooks/useRevealOnScroll';
import useBlogPosts from '../hooks/useBlogPosts';
import { tint } from '../utils/textStyles';
import { isDarkTheme } from '../utils/themeTraits';
import { HEADER_HEIGHT_PX, HEADER_HEIGHT_SX } from './layoutMetrics';
import HeaderProfileMenu from './HeaderProfileMenu';

// The bar is the tenant's own colour from the first frame and stays there — no wipe, no
// sweep, nothing washing over it. It used to arrive white and have the colour wiped over
// it, which on a #000 header meant the whole entrance was spent somewhere on the grey ramp.
//
// It does not arrive first either: it drops in after the hero image and the welcome card
// below it have started, so the page assembles in a sequence rather than all at once. Every
// page pays this delay, not only the home page — it is the one entrance the header has.
const HEADER_DELAY_MS = 0;
const HEADER_DURATION_MS = 200;

// The nav is a hand-written list whose length changes with the tenant's flags, so the
// stagger is addressed by position rather than by index — and the dividers Stack injects
// between the links take their own place in it, which is what makes the cascade read as one
// sweep across the bar rather than as items appearing in pairs.
//
// Positions are counted with `nth-of-type`, not `nth-child`: emotion flags `nth-child` as
// unsafe for server-side rendering and logs a warning for every rule, every time the header
// renders. The children alternate — every link is an `<a>` (NavLink and HashLink both
// render one) and every divider an `<hr>` — so link k sits at position 2k-1 and divider k at
// 2k, and the two `of-type` counts give back exactly the order `nth-child` had.
const NAV_DURATION_MS = HEADER_DURATION_MS;
const NAV_STAGGER_MS = 40;
// Measured from the page load rather than from the bar's arrival, so it carries the
// header's own delay: the links start surfacing while the bar is still coming down.
const NAV_BASE_DELAY_MS = HEADER_DELAY_MS + 500;
// Nine fixed links plus the tenant's menu posts. Past this a link still animates, it just
// stops taking its place in the cascade.
const NAV_MAX_LINKS = 16;

const navDelay = (position) => ({ animationDelay: `${NAV_BASE_DELAY_MS + position * NAV_STAGGER_MS}ms` });

const navItemIn = keyframes`
  from { opacity: 0; transform: translate3d(0, -14px, 0); }
  to   { opacity: 1; transform: none; }
`;

const navStaggerSx = (isVisible) => ({
  '& > *': {
    opacity: 0,
    animation: isVisible ? `${navItemIn} ${NAV_DURATION_MS}ms ${REVEAL_EASING} both` : 'none',
    ...navDelay(0),
  },
  ...Object.fromEntries(
    Array.from({ length: NAV_MAX_LINKS }, (_, index) => [
      `& > a:nth-of-type(${index + 1})`,
      navDelay(2 * index),
    ])
  ),
  ...Object.fromEntries(
    Array.from({ length: NAV_MAX_LINKS }, (_, index) => [
      `& > hr:nth-of-type(${index + 1})`,
      navDelay(2 * index + 1),
    ])
  ),
  '@media (prefers-reduced-motion: reduce)': {
    '& > *': { opacity: 1, animation: 'none' },
  },
});

// The links are the tenant's `headerColor`, label and icon both: white on the dark bars, black
// on a white one. On a dark theme the tenant's primary colour is in the lines
// on the bar — the dividers between the links and the underline that runs out under one on
// hover — never in the lettering. Paper draws those lines in the links' own colour.
const NAV_ITEM_COLOR = '#fff';

const navItemColor = (displayConfig) => displayConfig?.headerColor || NAV_ITEM_COLOR;

const navLineColor = (displayConfig) =>
  isDarkTheme(displayConfig)
    ? displayConfig?.primaryColor || navItemColor(displayConfig)
    : navItemColor(displayConfig);

// The line that runs out under a link on hover: the tenant's `headerHoverColor`, falling back to
// the divider colour -- which is what it always was before the key existed.
const navUnderlineColor = (displayConfig) => displayConfig?.headerHoverColor || navLineColor(displayConfig);

// The mobile menu. The dark theme keeps it the bar's black, with the primary colour in its
// dividers as on the bar; paper opens it as a white sheet in the tenant's ink, with neutral
// hairlines — white dividers would vanish on it.
const drawerPaperSx = (displayConfig) => (isDarkTheme(displayConfig)
  ? { backgroundColor: displayConfig?.headerBackground || '#000', color: navItemColor(displayConfig) }
  : { backgroundColor: '#fff', color: displayConfig?.color || '#000' });

const drawerDividerColor = (displayConfig) => (isDarkTheme(displayConfig)
  ? tint(navLineColor(displayConfig), 0.4)
  : tint(displayConfig?.color || '#000', 0.1));

const navButtonSx = (displayConfig) => ({
  fontWeight: 'bold',
  fontSize: 14,
  px: 1.25,
  whiteSpace: 'nowrap',
  position: 'relative',
  color: navItemColor(displayConfig),
  '& .MuiButton-startIcon': {
    mr: 0.75,
    color: navItemColor(displayConfig),
    '& > *:first-of-type': { fontSize: 18 },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 4,
    height: '2px',
    backgroundColor: navUnderlineColor(displayConfig),
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1)',
  },
  '&:hover::after': { transform: 'scaleX(1)' },
  '@media (prefers-reduced-motion: reduce)': {
    '&::after': { transition: 'none' },
  },
});

const Header = ({ onAuthChange }) => {
  const theme = useTheme();
  const downLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { tenant, displayConfig, hasSeveralProviders } = useTenant();
  const hasHomePage = tenant?.displayConfig?.hasHomePage;
  // With several providers the services shown are the chosen provider's, so the menu asks first.
  const servicesRoute = hasSeveralProviders ? Routes.PROVIDERS : Routes.SUPPLY;
  const hasAbout = displayConfig?.homePage?.about?.paragraphs?.length > 0;
  const hasGallery = displayConfig?.homePage?.gallery?.images?.length > 0;
  const menuPosts = useBlogPosts().filter((post) => post.isInMenu);
  const tenantId = tenant?.tenantId

  const navSlotRef = useRef(null);
  const navRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  // A load animation, not a scroll reveal: the header drops in from above the viewport.
  // `onMount` is required rather than cosmetic — the drop parks the header its own height
  // above the fold, where an IntersectionObserver would never see it and it would stay
  // hidden for good. RootLayout remounts this component on login and logout (the
  // `headerKey` bump), which replays the drop — those are full page transitions anyway, so
  // it reads as the new page arriving rather than a glitch.
  const header = useRevealOnScroll({
    direction: 'down',
    distance: HEADER_HEIGHT_PX,
    duration: HEADER_DURATION_MS,
    delay: HEADER_DELAY_MS,
    onMount: true,
  });

  // The nav stays mounted even when it does not fit -- hidden and out of the flow -- so its
  // natural width remains measurable and the header can switch back once there is room.
  useLayoutEffect(() => {
    const slot = navSlotRef.current;
    const nav = navRef.current;

    if (!slot || !nav || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => setIsCompact(nav.scrollWidth > slot.clientWidth);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen((prevDrawerOpen) => !prevDrawerOpen);
  };

  return (
    <AppBar ref={header.ref} position="sticky" sx={{ top: 0,
      //backgroundColor: { xs: "black", sm: "green", md: "blue", lg: "red", xl: "pink", xxl: "purple", xxxl: "orange" },
      ...header.revealSx,
      backgroundColor: displayConfig?.headerBackground || '#000',
      }}>
      <Container maxWidth="xl" disableGutters={downLg}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {/* In compact mode this button *is* the nav, at the bar's left edge before the logo, so it
              takes the base delay the first link would otherwise have had. */}
          {isCompact && (
            <IconButton
              onClick={handleDrawerToggle}
              aria-label="Menü megnyitása"
              sx={{
                ml: 0.5,
                mr: 1,
                flexShrink: 0,
                color: navItemColor(displayConfig),
                opacity: 0,
                animation: header.isVisible
                  ? `${navItemIn} ${NAV_DURATION_MS}ms ${REVEAL_EASING} ${NAV_BASE_DELAY_MS}ms both`
                  : 'none',
                '@media (prefers-reduced-motion: reduce)': { opacity: 1, animation: 'none' },
              }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          )}

          {/* No colour of its own: the logo sits straight on the bar, so the slot it
              occupies is the header's colour for however long the png takes to arrive
              rather than a white block that then fills in. */}
          <Box height={HEADER_HEIGHT_SX} flexShrink={0}>
            <NavLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Box
                component="img"
                src={`/${tenantId}/logo.png`}
                alt="Company Logo"
                sx={{ height: HEADER_HEIGHT_SX, display: 'block' }}
              />
            </NavLink>
          </Box>

          <Box
            ref={navSlotRef}
            sx={{ position: "relative", flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end", alignItems: "center" }}
          >
            <Stack
              ref={navRef}
              direction="row"
              spacing={0.5}
              alignItems={"center"}
              divider={<Divider orientation="vertical" flexItem sx={{ borderColor: navLineColor(displayConfig), backgroundColor: navLineColor(displayConfig) }}/>}
              sx={{
                ...(isCompact
                  ? { position: "absolute", right: 0, visibility: "hidden", pointerEvents: "none" }
                  : { whiteSpace: "nowrap" }),
                ...navStaggerSx(header.isVisible),
              }}
            >
              {
                hasHomePage &&
                <NavLink index="true" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Button color="inherit" startIcon={<HomeIcon />} sx={navButtonSx(displayConfig)}>Főoldal</Button>
                </NavLink>
              }
              <NavLink to={Routes.BOOKING} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<EventAvailableIcon />} sx={navButtonSx(displayConfig)}>Foglalás</Button>
              </NavLink>
              <NavLink to={servicesRoute} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<DesignServicesIcon />} sx={navButtonSx(displayConfig)}>Szolgáltatások</Button>
              </NavLink>
              {
                hasAbout &&
                <SectionLink to={Routes.ABOUT} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Button color="inherit" startIcon={<InfoOutlinedIcon />} sx={navButtonSx(displayConfig)}>Rólunk</Button>
                </SectionLink>
              }
              {menuPosts.map((post) => (
                <SectionLink key={post.anchorId} to={post.route} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Button color="inherit" startIcon={<post.MenuIcon />} sx={navButtonSx(displayConfig)}>{post.menuLabel}</Button>
                </SectionLink>
              ))}
              {
                hasGallery &&
                <SectionLink to={Routes.GALLERY} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Button color="inherit" startIcon={<CollectionsIcon />} sx={navButtonSx(displayConfig)}>Galéria</Button>
                </SectionLink>
              }
              <SectionLink to={Routes.OPENING_HOURS} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<AccessTimeIcon />} sx={navButtonSx(displayConfig)}>Nyitvatartás</Button>
              </SectionLink>
              <SectionLink to={Routes.CONTACTS} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<ContactPhoneIcon />} sx={navButtonSx(displayConfig)}>Kapcsolatok</Button>
              </SectionLink>
            </Stack>

          </Box>

          {/* The last item on every width: „Saját foglalások” and signing in or out live in it. It
              surfaces with the nav. */}
          <HeaderProfileMenu
            onAuthChange={onAuthChange}
            color={navItemColor(displayConfig)}
            paperSx={drawerPaperSx(displayConfig)}
            sx={{
              // Beside the bar's links it stands a little apart from the last one.
              ml: isCompact ? 0 : 3,
              mr: { xs: 1, lg: 0 },
              flexShrink: 0,
              opacity: 0,
              animation: header.isVisible
                ? `${navItemIn} ${NAV_DURATION_MS}ms ${REVEAL_EASING} ${NAV_BASE_DELAY_MS}ms both`
                : 'none',
              '@media (prefers-reduced-motion: reduce)': { opacity: 1, animation: 'none' },
            }}
          />
        </Stack>
      </Container>

      <Drawer
        // From the left, the side its button is on.
        anchor={"left"}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{ sx: drawerPaperSx(displayConfig) }}
      >
        <Box
          sx={{ width: 250 }}
          onClick={handleDrawerToggle}
          onKeyDown={handleDrawerToggle}
        >
          <Stack divider={<Divider orientation="horizontal" flexItem sx={{ borderColor: drawerDividerColor(displayConfig) }} />}>
            { hasHomePage && <MenuItem index label="Főoldal" icon={<HomeIcon fontSize="small" />} />}
            <MenuItem to={Routes.BOOKING} label="Foglalás" icon={<EventAvailableIcon fontSize="small" />} />
            <MenuItem to={servicesRoute} label="Szolgáltatások" icon={<DesignServicesIcon fontSize="small" />} />
            { hasAbout && <MenuItem to={Routes.ABOUT} label="Rólunk" icon={<InfoOutlinedIcon fontSize="small" />} componentType={SectionLink} />}
            { menuPosts.map((post) => (
              <MenuItem key={post.anchorId} to={post.route} label={post.menuLabel} icon={<post.MenuIcon fontSize="small" />} componentType={SectionLink} />
            ))}
            { hasGallery && <MenuItem to={Routes.GALLERY} label="Galéria" icon={<CollectionsIcon fontSize="small" />} componentType={SectionLink} />}
            <MenuItem to={Routes.OPENING_HOURS} label="Nyitvatartás" icon={<AccessTimeIcon fontSize="small" />} componentType={SectionLink} />
            <MenuItem to={Routes.CONTACTS} label="Kapcsolatok" icon={<ContactPhoneIcon fontSize="small" />} componentType={SectionLink} />
          </Stack>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default Header;
